/**
 * useCuratedCards — React Query hook for
 * `ContentReaderPort.listCuratedCards`.
 *
 * Wraps the active `ContentReaderPort` (resolved via {@link useContent})
 * in a `useQuery` call. Drives the Home curated-card strip
 * (`HomeCards.tsx`) which renders the curated strip entirely from the
 * published `home_card` payload. Since 029 US1 every curated card —
 * including `jasenedut` — is a published `home_card` row this hook
 * returns; there is no frontend-hardcoded card and no 5+1 split (the
 * hardcoded jasenedut tile was removed in 029 T014).
 *
 * D-046 binds React Query as the port-backed async-data layer for
 * `apps/web`. The adapter (`TrpcContentReader`, TA15) stays a plain
 * fetcher returning `Promise<readonly CuratedCard[]>`; this hook is
 * the single site that owns the cache + loading semantics for the
 * Home strip. See `apps/web/src/content/adapters/TrpcContentReader.ts`
 * file header §"D-046 — React Query integration" for the symmetry.
 *
 * queryKey design
 * ===============
 *
 * Shape: `['content', 'curated', limit, locale]`.
 *
 *   - `'content'` is the domain namespace shared by all four TA16
 *     hooks; isolates cache invalidation / prefetching to this module.
 *   - `'curated'` is the per-method discriminator.
 *   - `limit` is included because two callers asking for different
 *     soft caps must NOT alias each other; including it in the key
 *     keeps each truncation a distinct cache slot.
 *   - `locale` is included because the same curated set in different
 *     locales resolves to different rows; omitting it would alias them.
 *
 * The key is intentionally flat (no nested options object) so the
 * cache hits as soon as the same `(limit, locale)` pair is requested
 * again, regardless of object identity at the call site.
 *
 * Authoritative spec references
 * =============================
 *
 *   - `specs/003-content-and-knowledge/tasks.md` TA16, TA23.
 *   - `docs/governance/decisions/D-046.md` D-046.
 *   - `apps/web/src/content/ContentReaderPort.ts` (the port being wrapped).
 *   - `specs/029-cms-driven-member-surfaces/tasks.md` T010-T016 (the
 *     payload-driven curated strip + the draft-injection seam).
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { CuratedCard } from '../ContentReaderPort.js';
import { CONTENT_STALE_TIME } from './queryConfig.js';
import { useContent } from './useContent.js';

/**
 * Options for {@link useCuratedCards}.
 *
 * `limit` is a soft cap on the small (≈5 rows) curated set; absent
 * means "all". `locale` is forwarded verbatim to the port; the hook
 * does not impose a default of its own so that the cache key reflects
 * exactly what the caller passed.
 */
export interface UseCuratedCardsOptions {
  // `?: T | undefined` under `exactOptionalPropertyTypes`: callers pass a
  // state-held options bag where `limit` / `locale` may be `undefined`, and
  // the hook forwards a destructured `{ limit, locale }` straight to the
  // port. Present-undefined is semantically identical to absent here.
  readonly limit?: number | undefined;
  readonly locale?: string | undefined;
  /**
   * Gate the query (default `true`). The Home strip's draft-injection seam
   * (`HomeCards` `cards` prop, 029 T016) passes `enabled: false` so the
   * production read is disabled when a draft strip is injected client-side —
   * the preview renders the injected draft with no second server read (P4 /
   * P7 / FR-013). Mirrors `useArticle`'s `enabled` option.
   */
  readonly enabled?: boolean | undefined;
}

/**
 * Fetch the curated Home cards ordered by `curatedOrder` ascending.
 *
 * Returns the standard React Query result shape; consumers read
 * `data` (a `readonly CuratedCard[]` once resolved, `undefined` while
 * pending), `isPending`, `isError`, `error`, etc. The loading / empty
 * / error / populated states map onto React Query's matrix (029
 * FR-014) — `HomeCards.tsx` renders skeletons while pending, an
 * error-only-state strip on `isError` (Home stays reachable, FR-054),
 * a defined empty state for an empty set, and the authored cards once
 * `data` resolves.
 */
export function useCuratedCards(
  options: UseCuratedCardsOptions = {},
): UseQueryResult<readonly CuratedCard[], Error> {
  const { limit, locale, enabled } = options;
  const port = useContent();

  return useQuery<readonly CuratedCard[], Error>({
    queryKey: ['content', 'curated', limit, locale],
    queryFn: () => port.listCuratedCards({ limit, locale }),
    staleTime: CONTENT_STALE_TIME,
    enabled: enabled ?? true,
  });
}
